import { Tooltip } from 'bootstrap';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import { getDomainValueForCode } from '../../../library/Core/SmartFunctions';
import AlumniFiltersPanel from './AlumniFiltersPanel';
import AlumniGrid from './AlumniGrid';
import './burner-analysis.css';
import BurnerAnalysisService, {
    AlumniFilters,
    AlumniRow,
    InstituteHeader,
} from './BurnerAnalysisService';
import RunAnalysisModal, { RunTarget } from './RunAnalysisModal';
import { coverageColor, formatDate, relativeAgo } from './utils';

const HeaderInfo = ({ text }: { text: string }) => (
    <i
        className="bi bi-info-circle ms-1"
        style={{ fontSize: 11, color: '#64748b', cursor: 'help', verticalAlign: 'middle' }}
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        data-bs-html="true"
        data-bs-title={text}
        data-bs-custom-class="burner-analysis-tooltip"
    />
);

interface LoaderData {
    data: {
        instituteId: string;
        header: InstituteHeader;
        alumniList: AlumniRow[];
        totalAlumni: number;
        completionYears: number[];
        programLevels: string[];
        filters: AlumniFilters;
    };
}

const useDebounced = <T,>(value: T, delay = 300): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(t);
    }, [value, delay]);
    return debounced;
};

const countActive = (f: AlumniFilters): number => {
    const single = [f.programLevel, f.passOutYear, f.linkedinExists, f.profilePictureExists, f.outreachExists, f.engagementExists];
    return single.filter((v) => v && String(v).length > 0).length + (f.profileCompletionRanges?.length ?? 0);
};

const BurnerAnalysisAlumni = () => {
    const { data } = useLoaderData() as LoaderData;
    const navigate = useNavigate();
    const { globalState } = useContext(GlobalContext);
    const stateLike: any = { domain: globalState?.domain ?? new Map() };

    const PAGE_SIZE = 50;
    const statsRef = useRef<HTMLDivElement | null>(null);
    const [header, setHeader] = useState<InstituteHeader>(data.header);
    const [filters, setFilters] = useState<AlumniFilters>(data.filters);
    const [rows, setRows] = useState<AlumniRow[]>(data.alumniList);
    const [filteredCount, setFilteredCount] = useState<number>(data.totalAlumni);
    const [showFilters, setShowFilters] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [modalTarget, setModalTarget] = useState<RunTarget | null>(null);

    const filtersForKey = useMemo(() => {
        const { limit, offset, ...rest } = filters;
        return rest;
    }, [filters]);
    const debouncedFiltersBody = useDebounced(filtersForKey, 350);
    const filterKey = useMemo(() => JSON.stringify(debouncedFiltersBody), [debouncedFiltersBody]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        BurnerAnalysisService.searchInstituteAlumni(data.instituteId, {
            ...debouncedFiltersBody,
            limit: PAGE_SIZE,
            offset: 0,
        })
            .then((res) => {
                if (cancelled) return;
                setRows(res.data.alumni ?? []);
                setFilteredCount(res.data.total ?? 0);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey]);

    useEffect(() => {
        if (!statsRef.current) return;
        const els = statsRef.current.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tips = Array.from(els).map((el) => new Tooltip(el));
        return () => tips.forEach((t) => t.dispose());
    }, [header]);

    const handleLoadMore = () => {
        if (isLoadingMore || rows.length >= filteredCount) return;
        setIsLoadingMore(true);
        BurnerAnalysisService.searchInstituteAlumni(data.instituteId, {
            ...debouncedFiltersBody,
            limit: PAGE_SIZE,
            offset: rows.length,
        })
            .then((res) => {
                setRows((prev) => [...prev, ...(res.data.alumni ?? [])]);
                setFilteredCount(res.data.total ?? filteredCount);
            })
            .finally(() => setIsLoadingMore(false));
    };

    const reloadHeader = () => {
        BurnerAnalysisService.getInstituteHeader(data.instituteId).then((res) => setHeader(res.data));
    };

    const onRunCompleted = () => {
        reloadHeader();
        BurnerAnalysisService.searchInstituteAlumni(data.instituteId, {
            ...debouncedFiltersBody,
            limit: PAGE_SIZE,
            offset: 0,
        }).then((res) => {
            setRows(res.data.alumni ?? []);
            setFilteredCount(res.data.total ?? 0);
        });
    };

    const cityLabel = header.cityCode
        ? getDomainValueForCode(header.cityCode, 'CITY_CODE', stateLike) || header.cityCode
        : '—';
    const groupResolved = header.instituteGroupId
        ? getDomainValueForCode(header.instituteGroupId, 'SCHOOL_GROUP', stateLike)
        : '';
    const groupLabel =
        groupResolved && String(groupResolved) !== String(header.instituteGroupId) ? groupResolved : '—';
    const typeLabel = header.instituteTypeCode
        ? getDomainValueForCode(header.instituteTypeCode, 'INSTITUTE_TYPE_CODE', stateLike) ||
          String(header.instituteTypeCode).replace(/_/g, ' ')
        : '—';

    const donePct =
        Number(header.totalAlumni) > 0
            ? Math.round((Number(header.burnersDone) / Number(header.totalAlumni)) * 100)
            : 0;
    const activeColor = coverageColor(Number(header.activeWithBurnerPct));

    const activeFilterCount = countActive(filters);

    const renderHeaderRunButton = () => {
        if (Number(header.totalAlumni) === 0) {
            return null;
        }
        if (!header.isRunAllowed && header.cooldownDaysRemaining > 0) {
            return (
                <button
                    type="button"
                    className="btn btn-sm"
                    disabled
                    title={`Available in ${header.cooldownDaysRemaining}d (cooldown: 6 months)`}
                    style={{
                        background: '#f8fafc',
                        border: '0.5px solid #cbd5e1',
                        color: '#94a3b8',
                        cursor: 'not-allowed',
                    }}>
                    In {header.cooldownDaysRemaining}d
                </button>
            );
        }
        return (
            <button
                type="button"
                className="btn btn-sm"
                onClick={() =>
                    setModalTarget({
                        type: 'institute',
                        id: header.instituteId,
                        name: header.instituteName,
                        totalAlumni: header.totalAlumni,
                    })
                }
                title={header.lastBurnerRun ? `Last run: ${formatDate(header.lastBurnerRun)}` : 'Last run: Never'}
                style={{
                    background: '#e6f1fb',
                    border: '0.5px solid #185fa5',
                    color: '#0c447c',
                    fontWeight: 500,
                }}>
                ▶ Run institute analysis
            </button>
        );
    };

    return (
        <div className="main flex-1">
            <div className="mb-4">
                <div className="white-block white-block-notopborderradius p-0 h-100">
                    <div className="p-4 p-md-5">
                        <div className="font-13 text-muted mb-3">
                            <a
                                role="button"
                                className="text-primary"
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate('/support/burner-analysis')}>
                                ← Burner analysis
                            </a>
                            <span className="mx-2" style={{ color: '#cbd5e1' }}>
                                /
                            </span>
                            <span className="font-500" style={{ color: '#1e293b' }}>
                                {header.instituteName}
                            </span>
                        </div>

                        <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                            <div>
                                <h2 className="font-26 font-600 mb-0">{header.instituteName}</h2>
                                <p className="font-13 text-muted mb-0">
                                    {cityLabel} &nbsp;·&nbsp; {groupLabel} &nbsp;·&nbsp; {typeLabel}
                                </p>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                                <div className="text-end font-12 text-muted">
                                    <div>
                                        Last institute analysis:{' '}
                                        <strong style={{ color: '#1e293b' }}>
                                            {header.lastBurnerRun ? formatDate(header.lastBurnerRun) : 'Never'}
                                        </strong>
                                    </div>
                                    {header.lastBurnerRun && <div>{relativeAgo(header.lastBurnerRun)}</div>}
                                </div>
                                {renderHeaderRunButton()}
                            </div>
                        </div>

                        <div ref={statsRef} className="d-flex flex-wrap gap-2 mb-3">
                            {[
                                { l: 'Total alumni', v: Number(header.totalAlumni).toLocaleString() },
                                {
                                    l: 'Burners completed',
                                    v: Number(header.burnersDone).toLocaleString(),
                                    s: `${donePct}% of total`,
                                    tip: 'Count of alumni profiles in this institute that have at least one burner-analysis row.<br/>Shown alongside the % of total alumni this represents.',
                                },
                                {
                                    l: 'Active profiles w/ burner',
                                    v: Number(header.activeDone).toLocaleString(),
                                    s: `${Number(header.activeWithBurnerPct)}% of burners`,
                                    c: activeColor,
                                    tip: 'Of all alumni who have a burner-analysis row in this institute, the count (and %) whose status is one of:<br/>ACTIVE, PROFILE_IN_PROGRESS, PENDING_EMAIL_VERIFICATION.<br/><br/><b>Formula:</b> active alumni with burner ÷ alumni with burner × 100<br/><br/>Percentage colour: green ≥ 75%, amber 50–74%, red &lt; 50%.',
                                },
                                {
                                    l: 'Raw / semi / dec w/ burner',
                                    v: Number(header.nonActiveDone).toLocaleString(),
                                    s: `${Number(header.nonActiveWithBurnerPct)}% of burners`,
                                    tip: 'Of all alumni who have a burner-analysis row in this institute, the count (and %) whose status is one of:<br/>RAW_PROFILE, SEMI_VERIFIED_USER.<br/><br/><b>Formula:</b> non-active alumni with burner ÷ alumni with burner × 100',
                                },
                            ].map((card: any) => (
                                <div
                                    key={card.l}
                                    className="flex-fill"
                                    style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', minWidth: 180 }}>
                                    <div
                                        className="font-500"
                                        style={{
                                            fontSize: 11,
                                            color: '#64748b',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.4,
                                            marginBottom: 4,
                                        }}>
                                        {card.l}
                                        {card.tip && <HeaderInfo text={card.tip} />}
                                    </div>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: card.c ?? '#1e293b' }}>
                                        {card.v}
                                    </div>
                                    {card.s && <div className="font-11 text-muted">{card.s}</div>}
                                </div>
                            ))}
                        </div>

                        <div
                            style={{
                                background: '#f8fafc',
                                border: '0.5px solid rgba(15,23,42,0.08)',
                                borderRadius: 12,
                                padding: 18,
                                marginBottom: 14,
                            }}>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-3">
                                    <button
                                        type="button"
                                        className="btn btn-sm"
                                        onClick={() => setShowFilters((v) => !v)}
                                        style={{
                                            background: '#fff',
                                            border: '0.5px solid rgba(15,23,42,0.15)',
                                        }}>
                                        <i className="icon-filter font-13 me-2"></i>
                                        Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                                    </button>
                                    {(activeFilterCount > 0 || (filters.search && filters.search.length > 0)) && (
                                        <a
                                            role="button"
                                            className="font-13 font-500 text-danger"
                                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                                            onClick={() => setFilters(BurnerAnalysisService.defaultAlumniFilters())}>
                                            Clear all
                                        </a>
                                    )}
                                </div>
                                <input
                                    value={filters.search ?? ''}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value, offset: 0 })}
                                    placeholder="🔍 Search by name, email, mobile…"
                                    style={{
                                        padding: '7px 10px',
                                        border: '0.5px solid rgba(15,23,42,0.15)',
                                        borderRadius: 8,
                                        fontSize: 13,
                                        width: 310,
                                        maxWidth: '100%',
                                        background: '#fff',
                                    }}
                                />
                            </div>
                            <AlumniFiltersPanel
                                filters={filters}
                                completionYears={data.completionYears ?? []}
                                programLevels={data.programLevels ?? []}
                                onChange={setFilters}
                                show={showFilters}
                            />
                        </div>

                        {isLoading && (
                            <div className="text-center text-muted font-13 py-2">Loading alumni…</div>
                        )}

                        <AlumniGrid
                            rows={rows}
                            totalUnfiltered={filteredCount}
                            hasMore={rows.length < filteredCount}
                            isLoadingMore={isLoadingMore}
                            onLoadMore={handleLoadMore}
                            onRunAlumni={(alumni) =>
                                setModalTarget({
                                    type: 'alumni',
                                    id: alumni.alumniId,
                                    name: alumni.alumniName,
                                    instituteId: data.instituteId,
                                })
                            }
                        />
                    </div>
                </div>
            </div>

            {modalTarget && (
                <RunAnalysisModal
                    target={modalTarget}
                    onClose={() => setModalTarget(null)}
                    onCompleted={onRunCompleted}
                />
            )}
        </div>
    );
};

export default BurnerAnalysisAlumni;
